// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { PageHero, PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { ImageTextSection } from "@/components/ImageTextSection";
import { FeatureGrid } from "@/components/FeatureGrid";
import { TabsSection } from "@/components/TabsSection";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Icons
import { Truck, RotateCcw, Rocket, LayoutGrid, Zap } from "lucide-react";

// Images
import heroVideo from "@/assets/video/hero-ftl.mp4";
import ftlImage from "@/assets/ftl.png";
import ftlImage2 from "@/assets/ftl2.png";
import ftlClaImage from "@/assets/ftl-cla.png";
import uavImage from "@/assets/strategic.png";
import ftlTrilhoImage from "@/assets/ftl-trilho.png";
import ftlAerialImage from "@/assets/ftl-aerial.png";
import ftlSpace from "@/assets/ftl-espaco.png";

export const Route = createFileRoute("/$lang/services/ftl")({
  head: () => ({
    meta: [
      { title: i18n.t("services/ftl:metaTitle", "Liquid Training Rocket (FTL) — BIZU Space") },
      {
        name: "description",
        content: i18n.t("services/ftl:metaDesc",
          "The FTL-Perseu is a sounding rocket designed around the ARION liquid engine, representing the first rocket designed in Brazil to rely exclusively on liquid propulsion."
        ),
      },
      { property: "og:title", content: i18n.t("services/ftl:metaTitle", "Liquid Training Rocket (FTL) — BIZU Space") },
      {
        property: "og:description",
        content: i18n.t("services/ftl:metaOgDesc",
          "A mobile, reusable, and responsive suborbital flight platform designed for launch center training and payload testing."
        ),
      },
      { property: "og:url", content: "/services/ftl" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/services/ftl" }],
  }),
  component: FtlPage,
});

function FtlPage() {
  const { t } = useTranslation("services/ftl");

  const tacticalFeatures = [
    {
      title: t("features.f1", "Mobile"),
      icon: Truck,
    },
    {
      title: t("features.f2", "Recoverable and Reusable"),
      icon: RotateCcw,
    },
    {
      title: t("features.f3", "Responsive Launch Campaigns"),
      icon: Rocket,
    },
    {
      title: t("features.f4", "Low Operational Footprint"),
      icon: LayoutGrid,
    },
    {
      title: t("features.f5", "Rapid Deployment Capability"),
      icon: Zap,
    },
  ];

  const tabData = {
    /*
    rato: {
      title: t("tabs.rato.title", "UAV ASSISTED TAKE-OFF (RATO)"),
      buttonLabel: t("tabs.rato.buttonLabel", "UAV ASSISTED TAKE-OFF (RATO)"),
      image: uavImage,
      imagePosition: "left" as const,
      variant: "split" as const,
      paragraphs: [
        t("tabs.rato.p1", "Military UAVs often require long runways for takeoff, limiting deployment flexibility and reducing mission range due to the fuel consumed during departure."),
        t("tabs.rato.p2", "The FTL propulsion module can be adapted as a reusable Rocket-Assisted Take-Off (RATO) system, enabling operations from short runways, remote locations, and naval platforms."),
        t("tabs.rato.p3", "By reducing the energy required from the aircraft itself during takeoff, the system can increase payload capacity, extend operational range, and expand deployment options."),
      ],
      bullets: [],
    },
    */
    training: {
      title: t("tabs.training.title", "LAUNCH CENTER TRAINING"),
      buttonLabel: t("tabs.training.buttonLabel", "Launch Center Training"),
      image: ftlTrilhoImage,
      imagePosition: "right" as const,
      variant: "split" as const,
      paragraphs: [
        t("tabs.training.p1", "Building rockets is only half the challenge. Launching them is the other half."),
        t("tabs.training.p2", "Launching rockets requires specialized personnel, infrastructure, procedures, and safety systems operating in coordination."),
        t("tabs.training.p3", "The FTL was specifically designed to support launch center training activities by providing a realistic, low-cost, and repeatable flight vehicle."),
      ],
      listTitle: t("tabs.training.listTitle", "Applications include:"),
      bullets: [
        t("tabs.training.bullets.b1", "Personnel qualification"),
        t("tabs.training.bullets.b2", "Operational rehearsals"),
        t("tabs.training.bullets.b3", "New equipment validation"),
        t("tabs.training.bullets.b4", "Ground system verification"),
        t("tabs.training.bullets.b5", "Launch campaign training"),
        t("tabs.training.bullets.b6", "Range safety exercises"),
      ],
      postParagraphs: [
        t("tabs.training.postParagraphs.p1", "The platform allows organizations to build operational experience while reducing costs and risks compared to large launch vehicles."),
      ],
    },
    testing: {
      title: t("tabs.testing.title", "SUBORBITAL TEST MISSIONS"),
      buttonLabel: t("tabs.testing.buttonLabel", "Suborbital Test Missions"),
      image: ftlClaImage,
      imagePosition: "left" as const,
      variant: "split" as const,
      paragraphs: [
        t("tabs.testing.p1", "The FTL includes a protected payload compartment capable of accommodating payloads equivalent in size to a 2U CubeSat."),
        t("tabs.testing.p2", "The compartment is hermetically sealed and supported by a dual-event recovery system featuring parachutes, an auto-inflating flotation device, and a tracking beacon."),
        t("tabs.testing.p3", "During flight, payloads are exposed to realistic launch environments, including high accelerations, low-pressure conditions, and dynamic flight loads."),
        t("tabs.testing.p4", "These characteristics make the FTL an ideal platform for technology demonstrations, component qualification, and flight experimentation."),
      ],
      bullets: [],
    },
    /*
    target: {
      title: t("tabs.target.title", "AERIAL TARGET ROCKET"),
      buttonLabel: t("tabs.target.buttonLabel", "Aerial Target Rocket"),
      image: ftlAerialImage,
      imagePosition: "right" as const,
      variant: "split" as const,
      paragraphs: [
        t("tabs.target.p1", "The FTL can also be configured as a high-speed aerial target vehicle for military training exercises."),
      ],
      listTitle: t("tabs.target.listTitle", "Its supersonic flight capability makes it suitable for evaluating and training:"),
      bullets: [
        t("tabs.target.bullets.b1", "Ground-to-air defense systems"),
        t("tabs.target.bullets.b2", "Air-to-air interception systems"),
        t("tabs.target.bullets.b3", "Naval air-defense systems"),
        t("tabs.target.bullets.b4", "Tracking and telemetry infrastructure"),
      ],
      postParagraphs: [
        t("tabs.target.postParagraphs.p1", "The platform provides a realistic and cost-effective target for operational readiness exercises and system validation campaigns."),
      ],
    },
    */
  };

  return (
    <PageShell>
      {/* HERO SECTION WITH VIDEO BACKGROUND */}
      <PageHero
        title={t("heroTitle", "Liquid Training Rocket (FTL)")}
        description={t("heroDesc", "The FTL-Perseu was developed around the ARION liquid rocket engine as a flight test platform for qualifying onboard systems and supporting launch center training activities.")}
        video={heroVideo}
      />

      <ImageTextSection
        title={t("tacticalTitle", "Design for Responsive and Operational Applications")}
        image={ftlSpace}
        description={
          <>
            <p>{t("tacticalDesc1", "The FTL was engineered for fast, safe, and simplified operations.")}</p>
            <p className="mt-4">
              {t("tacticalDesc2", "It uses High-Test Peroxide (HTP) and Jet-A fuel as propellants, selected for their storability, high density, worldwide availability, and operational safety.")}
            </p>
            <p className="mt-4">
              {t("tacticalDesc3", "The vehicle is powered by the ARION liquid rocket engine in its ablative configuration, optimized for ground-level operation.")}
            </p>
            <p className="mt-4">
              {t("tacticalDesc4", "This propulsion architecture enables rapid campaign turnaround and reduced operational burden, making the FTL particularly suitable for flight tests, equipment qualification, and responsive space operations.")}
            </p>
          </>
        }
      />

      <section className="bg-[#070707] py-24 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="mb-16 text-center">
            <h2 className="display mt-5 text-4xl uppercase md:text-5xl">
              {t("featuresTitle", "Special Features")}
            </h2>
          </Reveal>
          <FeatureGrid items={tacticalFeatures} columns={5} centered />
        </div>
      </section>

      <ImageTextSection
        title={t("engineeredTitle", "Engineered for Real Operational Conditions")}
        image={ftlClaImage}
        variant="background"
        description={
          <>
            <p>
              {t("engineeredDesc1", "The FTL is a 4-meter-long rocket with a total launch mass of approximately 70 kg, including propellant.")}
            </p>
            <p className="mt-4">
              {t("engineeredDesc2", "The vehicle incorporates a recovery system featuring parachutes, an auto-inflating flotation device, and a radio beacon, enabling both land and sea recovery operations.")}
            </p>
            <p className="mt-4 mb-2">{t("engineeredListTitle", "Its avionics suite includes:")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("engineeredBullets.b1", "S-band telemetry transmitter")}</li>
              <li>{t("engineeredBullets.b2", "Onboard cameras")}</li>
              <li>{t("engineeredBullets.b3", "Engine Control Unit (ECU)")}</li>
              <li>{t("engineeredBullets.b4", "Recovery beacon")}</li>
              <li>{t("engineeredBullets.b5", "Flight computer")}</li>
            </ul>
            <p className="mt-4">
              {t("engineeredDesc3", "An integrated inertial navigation system continuously records flight data, allowing precise trajectory reconstruction and post-flight analysis.")}
            </p>
          </>
        }
      />

      {/* IMAGE LATERAL SECTION */}
      <ImageTextSection
        title={t("propulsionTitle", "Storable Propellant Propulsion")}
        image={ftlImage2}
        imagePosition="right"
        description={
          <>
            <p>
              {t("propulsionDesc1", "Powered by the ARION engine, the FTL uses High-Test Peroxide (HTP) and Jet-A fuel — storable, safe-to-handle, and globally available propellants.")}
            </p>
            <p className="mt-4 mb-2">{t("propulsionListTitle", "Unlike cryogenic systems, these propellants:")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("propulsionBullets.b1", "Do not require extremely low storage temperatures")}</li>
              <li>{t("propulsionBullets.b2", "Eliminate boil-off losses")}</li>
              <li>{t("propulsionBullets.b3", "Reduce ground infrastructure requirements")}</li>
              <li>{t("propulsionBullets.b4", "Enable extended launch readiness")}</li>
              <li>{t("propulsionBullets.b5", "Allow the use of commercially available industrial components")}</li>
            </ul>
            <p className="mt-4">
              {t("propulsionDesc2", "The simplicity of HTP-based propulsion significantly reduces operational complexity while improving responsiveness and safety.")}
            </p>
            <p className="mt-4">
              {t("propulsionDesc3", "These characteristics make the FTL particularly well suited for launch center training, technology demonstrations, and space operations.")}
            </p>
          </>
        }
      />

      {/* APPLICATIONS - TABS DESIGN */}
      <section className="bg-black pt-28 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="text-center">
            <h2 className="display mt-5 text-4xl uppercase md:text-6xl animate-reveal">
              {t("appsTitle", "Mission Applications")}
            </h2>
          </Reveal>
        </div>
      </section>
      <TabsSection tabs={tabData} />

      {/* FTL OVERVIEW - FINAL SECTION WITH TABLE */}
      <section className="border-t border-white/10 bg-[#070707] py-28 text-white">
        <div className="container-bizu">
          <div className="grid gap-16 lg:grid-cols-2 items-stretch">
            <Reveal effect="left" className="h-full">
              <div className="relative h-full min-h-[350px] overflow-hidden rounded-3xl">
                <img
                  src={ftlImage}
                  alt="FTL Overview"
                  className="absolute inset-0 h-full w-full object-cover shadow-2xl"
                />
              </div>
            </Reveal>

            <Reveal effect="right" className="flex flex-col justify-center">
              <h2 className="display mb-10 text-4xl uppercase md:text-5xl">
                {t("overviewTitle", "FTL Overview")}
              </h2>
              <div className="overflow-hidden rounded-2xl border border-white/20 bg-white/5">
                <table className="w-full text-left text-lg">
                  <thead>
                    <tr className="border-b border-white/20 bg-white/10">
                      <th className="px-6 py-4 font-bold uppercase tracking-wider text-white">
                        {t("table.parameter", "Parameter")}
                      </th>
                      <th className="px-6 py-4 font-bold uppercase tracking-wider text-white">
                        {t("table.value", "Value")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    <tr className="transition hover:bg-white/5">
                      <td className="px-6 py-4 font-semibold text-white/80">
                        {t("table.length", "Length")}
                      </td>
                      <td className="px-6 py-4 text-accent">4 m</td>
                    </tr>
                    <tr className="transition hover:bg-white/5">
                      <td className="px-6 py-4 font-semibold text-white/80">
                        {t("table.launchMass", "Launch Mass")}
                      </td>
                      <td className="px-6 py-4 text-accent">70 kg</td>
                    </tr>
                    <tr className="transition hover:bg-white/5">
                      <td className="px-6 py-4 font-semibold text-white/80">
                        {t("table.propulsion", "Propulsion")}
                      </td>
                      <td className="px-6 py-4 text-accent">
                        {t("table.motorDesc", "ARION Liquid Rocket Engine")}
                      </td>
                    </tr>
                    <tr className="transition hover:bg-white/5">
                      <td className="px-6 py-4 font-semibold text-white/80">
                        {t("table.propellants", "Propellants")}
                      </td>
                      <td className="px-6 py-4 text-accent">HTP / Jet-A</td>
                    </tr>
                    <tr className="transition hover:bg-white/5">
                      <td className="px-6 py-4 font-semibold text-white/80">
                        {t("table.recovery", "Recovery")}
                      </td>
                      <td className="px-6 py-4 text-accent">
                        {t("table.recoveryDesc", "Parachute Land & Sea Recovery")}
                      </td>
                    </tr>
                    <tr className="transition hover:bg-white/5">
                      <td className="px-6 py-4 font-semibold text-white/80">
                        {t("table.telemetry", "Telemetry")}
                      </td>
                      <td className="px-6 py-4 text-accent">S-Band</td>
                    </tr>
                    <tr className="transition hover:bg-white/5">
                      <td className="px-6 py-4 font-semibold text-white/80">
                        {t("table.payload", "Payload Capacity")}
                      </td>
                      <td className="px-6 py-4 text-accent">
                        {t("table.payloadDesc", "Up to 2U equivalent")}
                      </td>
                    </tr>
                    <tr className="transition hover:bg-white/5">
                      <td className="px-6 py-4 font-semibold text-white/80">
                        {t("table.reusability", "Reusability")}
                      </td>
                      <td className="px-6 py-4 text-accent">
                        {t("table.reusable", "Reusable")}
                      </td>
                    </tr>
                    <tr className="transition hover:bg-white/5">
                      <td className="px-6 py-4 font-semibold text-white/80">
                        {t("table.applications", "Applications")}
                      </td>
                      <td className="px-6 py-4 text-accent">
                        {t("table.appsDesc", "Training, Testing, Suborbital Missions")}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
